import React, { useState, useEffect, useRef, useCallback } from "react";
import { addEligibilityCriteria, deleteEligibilityCriteria, updateEligibilityCriteria } from "../../api/api";
import { useGlobalData } from "../../context/GlobalDataContext";
import "../superadmin/EligibilityAdminPanel.css";

function HREligibilityAdminPanel() {
  const { threeTable, fetchThreeTableSection } = useGlobalData();
  const [formData, setFormData] = useState({
    jobTitle: "",
    education: "",
    skills: "",
    experience: "",
    location: "",
    minAge: "",
    maxAge: "",
    gender: "all",
    status: "pending"
  });
  const [jobTitleExtra, setJobTitleExtra] = useState("");
  const [criterias, setCriterias] = useState([]);
  const [editId, setEditId] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [criteriasOffset, setCriteriasOffset] = useState(0);
  const [criteriasHasMore, setCriteriasHasMore] = useState(true);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const listRef = useRef(null);
  const PAGE_LIMIT = 29;
  const [darkMode, setDarkMode] = useState(() => localStorage.getItem('darkMode') === 'true');
  const role = localStorage.getItem('role');

  useEffect(() => {
    const syncDarkMode = () => setDarkMode(localStorage.getItem('darkMode') === 'true');
    window.addEventListener('storage', syncDarkMode);
    window.addEventListener('darkModeChanged', syncDarkMode);
    return () => {
      window.removeEventListener('storage', syncDarkMode);
      window.removeEventListener('darkModeChanged', syncDarkMode);
    };
  }, []);

  // Initial load (global state)
  useEffect(() => {
    let ignore = false;
    async function fetchInitial() {
      setIsLoading(true);
      await fetchThreeTableSection("eligibilitycriterias", false, { limit: PAGE_LIMIT, offset: 0 });
      if (!ignore) setJobs(threeTable.jobs || []);
      setIsLoading(false);
    }
    fetchInitial();
    return () => { ignore = true; };
    // eslint-disable-next-line
  }, []);

  // Sync criterias from global state
  useEffect(() => {
    setCriterias((threeTable.eligibilitycriterias || []).slice().reverse());
    setCriteriasOffset(threeTable.eligibilitycriterias?.length || 0);
    setCriteriasHasMore((threeTable.eligibilitycriterias?.length || 0) === PAGE_LIMIT);
    setJobs(threeTable.jobs || []);
    setIsLoading(false);
  }, [threeTable.eligibilitycriterias, threeTable.jobs]);

  // Infinite scroll fetch
  const fetchMoreCriterias = useCallback(async () => {
    if (!criteriasHasMore || isFetchingMore) return;
    setIsFetchingMore(true);
    await fetchThreeTableSection("eligibilitycriterias", false, { limit: PAGE_LIMIT, offset: criteriasOffset });
    setIsFetchingMore(false);
  }, [criteriasHasMore, isFetchingMore, criteriasOffset, fetchThreeTableSection]);

  // Infinite scroll handler
  const handleScroll = useCallback(() => {
    if (!criteriasHasMore || isFetchingMore) return;
    const el = listRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    if (rect.bottom <= window.innerHeight + 200) {
      fetchMoreCriterias();
    }
  }, [criteriasHasMore, isFetchingMore, fetchMoreCriterias]);

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  if (role !== 'hr') {
    return <div style={{ color: 'red', textAlign: 'center', marginTop: '2rem' }}>Access denied: Superadmin only</div>;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    const submitData = {
      ...formData,
      jobTitle: jobTitleExtra ? formData.jobTitle + " " + jobTitleExtra : formData.jobTitle,
      skills: formData.skills ? formData.skills : "",
      experience: formData.experience ? formData.experience : "",
      status: "pending"
    };
    if (editId) {
      await updateEligibilityCriteria(editId, submitData);
      setEditId(null);
    } else {
      await addEligibilityCriteria(submitData);
    }
    setFormData({ jobTitle: "", education: "", skills: "", experience: "", location: "", minAge: "", maxAge: "", gender: "all", status: "pending" });
    setJobTitleExtra("");
    await fetchThreeTableSection("eligibilitycriterias", true, { limit: PAGE_LIMIT, offset: 0 });
  };

  const handleDelete = async (id) => {
    if (window.confirm("Delete this eligibility criteria?")) {
      await deleteEligibilityCriteria(id);
      await fetchThreeTableSection("eligibilitycriterias", true, { limit: PAGE_LIMIT, offset: 0 });
    }
  };

  const handleModify = (criteria) => {
    setEditId(criteria._id);
    let mainTitle = criteria.jobTitle;
    let extra = "";
    const match = jobs.find(j => mainTitle.startsWith(j.title));
    if (match) {
      mainTitle = match.title;
      extra = criteria.jobTitle.slice(match.title.length).trim();
    }
    setFormData({
      jobTitle: mainTitle,
      education: criteria.education,
      skills: criteria.skills,
      experience: criteria.experience,
      location: criteria.location,
      minAge: criteria.minAge,
      maxAge: criteria.maxAge,
      gender: criteria.gender || "all",
      status: criteria.status || "pending"
    });
    setJobTitleExtra(extra);
  };

  // Approve handler
  const handleApprove = async (id) => {
    const criteria = criterias.find(c => c._id === id);
    if (!criteria) return;
    await updateEligibilityCriteria(id, { ...criteria, status: "approved" });
    await fetchThreeTableSection("eligibilitycriterias", true, { limit: PAGE_LIMIT, offset: 0 });
  };

  return (
    <div className={`eligibility-admin-panel${darkMode ? ' dark' : ''}`}> 
      <div className="resource-admin-panel__header">
        <div>
          <h2 className="resource-admin-panel__heading">🎯 Eligibility Admin Panel</h2>
          <p className="resource-admin-panel__subtext">Yahan se job eligibility criteria manage karo. Add, view, ya delete karo.</p>
        </div>
        <div className="resource-admin-panel__pill">Eligibility</div>
      </div>
      <form className="eligibility-admin-panel__form" onSubmit={handleSubmit}>
        {/* ...existing form fields... */}
        <div className="eligibility-admin-panel__field">
          <label className="eligibility-admin-panel__label">Job Title</label>
          <select
            className="eligibility-admin-panel__select"
            value={formData.jobTitle}
            onChange={e => setFormData({ ...formData, jobTitle: e.target.value })}
            required
          >
            <option value="" disabled>Select a job</option>
            {jobs.map(job => (
              <option key={job._id} value={job.title}>{job.title} ({job.slug})</option>
            ))}
          </select>
        </div>
        {/* ...rest of the form fields... */}
        {/* ...copy all form fields as before... */}
        <div className="eligibility-admin-panel__field">
          <label className="eligibility-admin-panel__label">Job Title ke last me add karne wala text (optional)</label>
          <input
            className="eligibility-admin-panel__input"
            type="text"
            placeholder="Job Title ke last me add hoga..."
            value={jobTitleExtra}
            onChange={e => setJobTitleExtra(e.target.value)}
          />
        </div>
        <div className="eligibility-admin-panel__field">
          <label className="eligibility-admin-panel__label">Gender</label>
          <select
            className="eligibility-admin-panel__select"
            value={formData.gender || "all"}
            onChange={e => setFormData({ ...formData, gender: e.target.value })}
            required
          >
            <option value="all">All</option>
            <option value="male">Male</option>
            <option value="female">Female</option>
            <option value="other">Other</option>
          </select>
        </div>
        <div className="eligibility-admin-panel__field">
          <label className="eligibility-admin-panel__label">Education Group</label>
          <select
            className="eligibility-admin-panel__select"
            value={formData.education}
            onChange={e => setFormData({ ...formData, education: e.target.value })}
            required
          >
            <option value="">Select Education Group</option>
            <option value="school">School/Pre-UG (10th, 12th, Diploma)</option>
            <option value="ug">UG (B.A., B.Sc., B.Com., B.Tech, BCA, BBA)</option>
            <option value="pg">PG (M.A., M.Sc., M.Com., M.Tech, MBA, MCA)</option>
            <option value="doctorate">Doctorate (PhD)</option>
          </select>
        </div>
        <div className="eligibility-admin-panel__field">
          <label className="eligibility-admin-panel__label">Skills</label>
          <input className="eligibility-admin-panel__input" placeholder="Skills" value={formData.skills} onChange={e => setFormData({ ...formData, skills: e.target.value })} />
        </div>
        <div className="eligibility-admin-panel__field">
          <label className="eligibility-admin-panel__label">Experience</label>
          <input className="eligibility-admin-panel__input" placeholder="Experience" value={formData.experience} onChange={e => setFormData({ ...formData, experience: e.target.value })} />
        </div>
        <div className="eligibility-admin-panel__field">
          <label className="eligibility-admin-panel__label">Location (State/UT)</label>
          <select
            className="eligibility-admin-panel__select"
            value={formData.location}
            onChange={e => setFormData({ ...formData, location: e.target.value })}
            required
          >
            <option value="">Select State/UT</option>
            <option value="India">India</option>
            <option value="Andhra Pradesh">Andhra Pradesh</option>
            <option value="Arunachal Pradesh">Arunachal Pradesh</option>
            <option value="Assam">Assam</option>
            <option value="Bihar">Bihar</option>
            <option value="Chhattisgarh">Chhattisgarh</option>
            <option value="Goa">Goa</option>
            <option value="Gujarat">Gujarat</option>
            <option value="Haryana">Haryana</option>
            <option value="Himachal Pradesh">Himachal Pradesh</option>
            <option value="Jharkhand">Jharkhand</option>
            <option value="Karnataka">Karnataka</option>
            <option value="Kerala">Kerala</option>
            <option value="Madhya Pradesh">Madhya Pradesh</option>
            <option value="Maharashtra">Maharashtra</option>
            <option value="Manipur">Manipur</option>
            <option value="Meghalaya">Meghalaya</option>
            <option value="Mizoram">Mizoram</option>
            <option value="Nagaland">Nagaland</option>
            <option value="Odisha">Odisha</option>
            <option value="Punjab">Punjab</option>
            <option value="Rajasthan">Rajasthan</option>
            <option value="Sikkim">Sikkim</option>
            <option value="Tamil Nadu">Tamil Nadu</option>
            <option value="Telangana">Telangana</option>
            <option value="Tripura">Tripura</option>
            <option value="Uttar Pradesh">Uttar Pradesh</option>
            <option value="Uttarakhand">Uttarakhand</option>
            <option value="West Bengal">West Bengal</option>
            <option value="Andaman and Nicobar Islands">Andaman and Nicobar Islands</option>
            <option value="Chandigarh">Chandigarh</option>
            <option value="Dadra and Nagar Haveli and Daman and Diu">Dadra and Nagar Haveli and Daman and Diu</option>
            <option value="Delhi">Delhi</option>
            <option value="Jammu and Kashmir">Jammu and Kashmir</option>
            <option value="Ladakh">Ladakh</option>
            <option value="Lakshadweep">Lakshadweep</option>
            <option value="Puducherry">Puducherry</option>
          </select>
        </div>
        <div className="eligibility-admin-panel__field">
          <label className="eligibility-admin-panel__label">Min Age</label>
          <select
            className="eligibility-admin-panel__select"
            value={formData.minAge}
            onChange={e => setFormData({ ...formData, minAge: e.target.value })}
            required
          >
            <option value="">Select Min Age</option>
            {Array.from({ length: 45 }, (_, i) => 16 + i).map(age => (
              <option key={age} value={age}>{age}</option>
            ))}
          </select>
        </div>
        <div className="eligibility-admin-panel__field">
          <label className="eligibility-admin-panel__label">Max Age</label>
          <select
            className="eligibility-admin-panel__select"
            value={formData.maxAge}
            onChange={e => setFormData({ ...formData, maxAge: e.target.value })}
            required
          >
            <option value="">Select Max Age</option>
            {Array.from({ length: 45 }, (_, i) => 16 + i).map(age => (
              <option key={age} value={age}>{age}</option>
            ))}
          </select>
        </div>
        <button className="eligibility-admin-panel__submit" type="submit">✅ Add Eligibility</button>
      </form>
      <div className="resource-admin-panel__section">
        <div className="resource-admin-panel__section-top">
          <div>
            <h3 className="resource-admin-panel__section-title">Saved Eligibilities</h3>
            <p className="resource-admin-panel__section-meta">{criterias.length} items</p>
          </div>
        </div>
        {isLoading ? (
          <div className="global-loader"><div className="skeleton-loader" /></div>
        ) : criterias.length === 0 ? (
          <p className="resource-admin-panel__empty">No eligibility criteria found</p>
        ) : (
          <div className="eligibility-admin-panel__list" ref={listRef} style={{ maxHeight: "800px", overflowY: "auto" }}>
            {criterias.map((criteria) => (
              <div key={criteria._id} className="eligibility-admin-panel__item">
                <div>
                  <p className="eligibility-admin-panel__item-title">{criteria.jobTitle}</p>
                  <p className="eligibility-admin-panel__item-subtitle">
                    Education Group: {criteria.education},
                    Gender: {criteria.gender ? (criteria.gender.charAt(0).toUpperCase() + criteria.gender.slice(1)) : 'All'},
                    Skills: {criteria.skills},
                    Experience: {criteria.experience},
                    Location: {criteria.location},
                    Age: {criteria.minAge}-{criteria.maxAge}
                  </p>
                  <p style={{ margin: '4px 0' }}>
                    Status: <span style={{
                      color: criteria.status === 'approved' ? 'green' : '#b59a00',
                      fontWeight: 'bold',
                      background: criteria.status === 'approved' ? '#e6ffe6' : '#fffbe6',
                      borderRadius: 4,
                      padding: '2px 8px',
                      fontSize: 13
                    }}>{criteria.status === 'approved' ? 'Approved' : 'Pending'}</span>
                  </p>
                </div>
                <div className="resource-admin-panel__actions" style={{display:'flex', flexDirection:'column', gap:'8px', alignItems:'flex-start'}}>
                  {criteria.status !== 'approved' && (
                    <>
                      <button className="resource-admin-panel__delete" onClick={() => handleDelete(criteria._id)}>Delete</button>
                      <button className="resource-admin-panel__modify" onClick={() => handleModify(criteria)}>Modify</button>
                      <button className="resource-admin-panel__approve" onClick={() => handleApprove(criteria._id)} style={{background:'#ffe066', color:'#b59a00', border:'1px solid #b59a00'}}>Approve</button>
                    </>
                  )}
                </div>
              </div>
            ))}
            {isFetchingMore && <div style={{ textAlign: "center", margin: 10 }}>Loading more...</div>}
            {!criteriasHasMore && !isFetchingMore && criterias.length > 0 && <div style={{ textAlign: "center", margin: 10, color: "#888" }}>No more eligibility criterias.</div>}
          </div>
        )}
      </div>
    </div>
  );
}

export default HREligibilityAdminPanel;
